import { useEffect } from 'react';
import {
  BrowserRouter, Routes, Route, useLocation, Navigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomePage from './pages/HomePage';
import PixViewerPage from './pages/PixViewerPage';
import PixRowPage from './pages/PixRowPage';
import {
  DEFAULT_LANG, LANGS, HTML_LANG, splitLangPath, localizedPath,
} from './i18n/routing';

// Scroll to top on route change, unless a hash is present.
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in document.documentElement.style ? 'instant' : 'auto' });
  }, [pathname, hash]);
  return null;
}

/**
 * A URL é a fonte da verdade do idioma.
 *
 * Mantém o i18next e o `<html lang>` em sincronia com o prefixo do
 * pathname. O atributo importa para além da estética: é o que leitores de
 * tela usam para escolher a pronúncia e o que o buscador lê para saber em
 * que língua a página está.
 */
function LangSync() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const { lang } = splitLangPath(pathname);
  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) i18n.changeLanguage(lang);
    document.documentElement.setAttribute('lang', HTML_LANG[lang] || 'pt-BR');
  }, [lang, i18n]);
  return null;
}

const PAGES = [
  { path: '', element: <HomePage /> },
  { path: 'pixviewer', element: <PixViewerPage /> },
  { path: 'pixrow', element: <PixRowPage /> },
];

/**
 * Redireciona URL desconhecida para a home DO MESMO IDIOMA.
 *
 * Antes o catch-all renderizava a home com status 200 em qualquer caminho
 * — um "soft 404": o buscador indexava `/qualquer-coisa` como duplicata da
 * home. Um redirect ao menos consolida o sinal num único endereço. Um 404
 * de verdade depende do servidor que hospeda o build.
 */
function NotFoundRedirect() {
  const { pathname } = useLocation();
  const { lang } = splitLangPath(pathname);
  return <Navigate to={localizedPath('/', lang)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      <LangSync />
      <Routes>
        {/* Português na raiz — as URLs já indexadas seguem valendo. */}
        {PAGES.map((p) => (
          <Route key={`root-${p.path}`} path={`/${p.path}`} element={p.element} />
        ))}
        {/* Demais idiomas, sob prefixo. `/pt/...` também resolve, para quem
            chegar por um link montado à mão. */}
        {LANGS.map((lang) =>
          PAGES.map((p) =>
            lang === DEFAULT_LANG && p.path === '' ? null : (
              <Route
                key={`${lang}-${p.path}`}
                path={`/${lang}/${p.path}`}
                element={p.element}
              />
            ),
          ),
        )}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
